import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaTimes,
  FaPlay,
  FaPause,
  FaSave,
  FaTrash,
  FaMinus,
  FaPlus,
  FaStop,
  FaStopCircle,
  FaMicrophone,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { toast, ToastContainer } from "react-toastify";
import { ragaScaleGroups, findScaleById } from "./ragaScaleData";
import { useUser } from "./UserContext";
import "./css/MelodyModal.css";
import { Databases, Query } from "appwrite";
import client from "./appwriteConfig";
import InstrumentAudioContext from "./InstrumentAudioContext";
import { usePatternInsSeq } from "./PatternInsSeqContext";
import { debounce } from "lodash";
import { ClipLoader } from "react-spinners";
import {
  chordProgressionGroups,
  findChordProgressionById,
} from "./chordProgressionData";

const databases = new Databases(client);

const generateNotes = () => {
  const notes = [];
  const noteNames = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
  ];
  for (let octave = 0; octave < 6; octave++) {
    for (const note of noteNames) {
      notes.push(`${note}${octave}`);
    }
  }
  return notes;
};

const keyToNoteMap = {
  z: "C2",
  s: "C#2",
  x: "D2",
  d: "D#2",
  c: "E2",
  v: "F2",
  g: "F#2",
  b: "G2",
  h: "G#2",
  n: "A2",
  j: "A#2",
  m: "B2",
  ",": "C3",
  q: "C3",
  2: "C#3",
  w: "D3",
  3: "D#3",
  e: "E3",
  r: "F3",
  5: "F#3",
  t: "G3",
  6: "G#3",
  y: "A3",
  7: "A#3",
  u: "B3",
  i: "C4",
  9: "C#4",
  o: "D4",
  0: "D#4",
  p: "E4",
};
const NOTE_ORDER = [
  "C",
  "C#",
  "D",
  "D#",
  "E",
  "F",
  "F#",
  "G",
  "G#",
  "A",
  "A#",
  "B",
];

const stripOctave = (note) => note.replace(/\d/g, "");

const normalizeNote = (note) => {
  const flatMap = {
    Db: "C#",
    Eb: "D#",
    Gb: "F#",
    Ab: "G#",
    Bb: "A#",
  };

  return flatMap[note] || note;
};

const getInterval = (root, note) => {
  const rootIndex = NOTE_ORDER.indexOf(root);
  const noteIndex = NOTE_ORDER.indexOf(note);

  if (rootIndex === -1 || noteIndex === -1) return null;

  return (noteIndex - rootIndex + 12) % 12;
};

const CHORD_TYPES = [
  { name: "", intervals: [0, 4, 7] }, // major
  { name: "m", intervals: [0, 3, 7] }, // minor
  { name: "dim", intervals: [0, 3, 6] },
  { name: "aug", intervals: [0, 4, 8] },

  { name: "sus2", intervals: [0, 2, 7] },
  { name: "sus4", intervals: [0, 5, 7] },

  { name: "5", intervals: [0, 7] },

  { name: " 6", intervals: [0, 4, 7, 9] },
  { name: "m 6", intervals: [0, 3, 7, 9] },

  { name: "7", intervals: [0, 4, 7, 10] },
  { name: "M 7", intervals: [0, 4, 7, 11] },
  { name: "m 7", intervals: [0, 3, 7, 10] },
  { name: "m M7", intervals: [0, 3, 7, 11] },

  { name: " dim7", intervals: [0, 3, 6, 9] },
  { name: "m7 ♭5", intervals: [0, 3, 6, 10] },

  { name: " a9", intervals: [0, 4, 7, 2] },
  { name: "m a9", intervals: [0, 3, 7, 2] },

  { name: " 9", intervals: [0, 4, 7, 10, 2] },
  { name: " M9", intervals: [0, 4, 7, 11, 2] },
  { name: "m 9", intervals: [0, 3, 7, 10, 2] },

  { name: "11", intervals: [0, 4, 7, 10, 2, 5] },
  { name: "m 11", intervals: [0, 3, 7, 10, 2, 5] },

  { name: "13", intervals: [0, 4, 7, 10, 2, 9] },
  { name: " M 13", intervals: [0, 4, 7, 11, 2, 9] },
  { name: "m 13", intervals: [0, 3, 7, 10, 2, 9] },

  { name: "7 ♭5", intervals: [0, 4, 6, 10] },
  { name: "7 #5", intervals: [0, 4, 8, 10] },
  { name: "7 ♭9", intervals: [0, 4, 7, 10, 1] },
  { name: "7 #9", intervals: [0, 4, 7, 10, 3] },
];

const findBestChordMatch = (intervals) => {
  const unique = [...new Set(intervals)].sort((a, b) => a - b);

  let best = null;

  CHORD_TYPES.forEach((type) => {
    const matches = type.intervals.every((i) => unique.includes(i));

    if (!matches) return;

    const extras = unique.filter((i) => !type.intervals.includes(i));

    const score = type.intervals.length * 100 - extras.length;

    if (!best || score > best.score) {
      best = {
        ...type,
        extras,
        score,
      };
    }
  });

  return best;
};
const EXTRA_NAMES = {
  1: "♭9",
  2: "a9",
  3: "#9",
  5: "a11",
  6: "♭5",
  8: "#5",
  9: "a13",
  10: "7",
  11: "M7",
};
const getChordDisplayName = (notesList) => {
  if (!notesList || notesList.length === 0) return "---";

  const cleanNotes = [
    ...new Set(notesList.map(stripOctave).map(normalizeNote)),
  ];

  if (cleanNotes.length === 1) return cleanNotes[0];

  if (cleanNotes.length === 2) return cleanNotes.join("-");

  let best = null;

  for (const root of cleanNotes) {
    const intervals = cleanNotes
      .map((note) => getInterval(root, note))
      .filter((i) => i !== null);

    const match = findBestChordMatch(intervals);

    if (!match) continue;

    const extensionText = match.extras
      .map((i) => EXTRA_NAMES[i] || "")
      .filter(Boolean)
      .join("");

    const name = `${root}${match.name}${extensionText}`;

    if (!best || match.score > best.score) {
      best = {
        score: match.score,
        name,
      };
    }
  }

  return best ? best.name : cleanNotes.join("-");
};
const MelodyModal = ({
  instrument,
  onClose,
  bpm,
  setBpm,
  selectedInstruments,
  setSelectedInstruments,
  initialPatternNo,
  onLoadRagaToPianoRoll,
  globalPlayingNotes = [],
  isGlobalPlaying = false,
  onPreviewInstrumentChange,
  trialDropdownLocked = false,
  openPlansModal = () => {},
  trialSeconds = 0,
  isTrial = false,
  stepCount = 16,
  isSwingOn = false,
  swingAmount = 0.35,
  onInstrumentChange = async () => {},
  onPatternSelectChange = async () => {},
}) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(
    InstrumentAudioContext.getInstVolume(instrument),
  );
  const [tempVolume, setTempVolume] = useState(volume);
  const [selectedPattern, setSelectedPattern] = useState("");
  const [patterns, setPatterns] = useState([]);
  const [grid, setGrid] = useState(
    Array(stepCount)
      .fill()
      .map(() => Array(generateNotes().length).fill(false)),
  );
  const intervalRef = useRef(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [activeNotes, setActiveNotes] = useState([]);
  const [currentBar, setCurrentBar] = useState(0);
  const [lastSavedPatternNo, setLastSavedPatternNo] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [recordingStarted, setRecordingStarted] = useState(false);
  const [octaveOffset, setOctaveOffset] = useState(0);
  const [transposeOffset, setTransposeOffset] = useState(0);
  const notes = generateNotes();
  const keyboardRef = useRef(null);
  const [playingNotes, setPlayingNotes] = useState([]);
  const [lastLedNotes, setLastLedNotes] = useState([]);
  const [currentInstrument, setCurrentInstrument] = useState(instrument);

  useEffect(() => {
    if (!instrument) return;

    const syncInstrumentFromGlobal = async () => {
      if (instrument === currentInstrument) return;

      const wasPlaying = isPlaying;
      const resumeStep = currentBarRef.current;

      // Stop the previous instrument and local interval.
      InstrumentAudioContext.stopAllNotes();

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      await InstrumentAudioContext.loadAudio(instrument);

      const nextVolume =
        InstrumentAudioContext.getInstVolume(instrument) ?? tempVolume;

      setCurrentInstrument(instrument);
      setVolume(nextVolume);
      setTempVolume(nextVolume);

      // Resume using the newly loaded instrument.
      if (wasPlaying && !isGlobalPlaying) {
        startPlayback(grid, resumeStep);
      }
    };

    syncInstrumentFromGlobal();
  }, [instrument]);
  const titleLedText = getChordDisplayName(
    playingNotes.length ? playingNotes : lastLedNotes,
  );
  useEffect(() => {
    if (globalPlayingNotes.length > 0) {
      setPlayingNotes(globalPlayingNotes);
      setLastLedNotes(globalPlayingNotes);
    } else {
      setPlayingNotes([]);
    }
  }, [globalPlayingNotes]);
  const currentBarRef = useRef(0);

  useEffect(() => {
    if (!isGlobalPlaying) return;

    setIsPlaying(false);
    setIsRecording(false);
    setRecordingStarted(false);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    currentBarRef.current = 0;
    setCurrentBar(0);

    setActiveNotes([]);
    setPlayingNotes([]);
    setLastLedNotes([]);

    InstrumentAudioContext.stopAllNotes();
  }, [isGlobalPlaying]);

  const [selectedRagaScale, setSelectedRagaScale] = useState("");
  const [isVolumeChanging, setIsVolumeChanging] = useState(false);
  const [lastSavedPatternName, setLastSavedPatternName] = useState("");
  const [chordMode, setChordMode] = useState("chord");
  const [touchMode, setTouchMode] = useState(false);
  const [selectedChordProgression, setSelectedChordProgression] = useState("");
  const {
    fetchPianoRollPatterns,
    savePianoRollPattern,
    deletePianoRollPattern,
    fetchNotesPianoRoll,
  } = usePatternInsSeq();
  useEffect(() => {
    return () => {
      stopPlayback();
      InstrumentAudioContext.stopAllNotes();
    };
  }, []);
  useEffect(() => {
    const loadPatterns = async () => {
      const refreshedPatterns = await fetchPianoRollPatterns();

      refreshedPatterns.sort(
        (a, b) => Number(a.patternNo) - Number(b.patternNo),
      );

      setPatterns(refreshedPatterns);
    };

    loadPatterns();
  }, [fetchPianoRollPatterns, isLoadingSave, isLoadingDelete]);

  useEffect(() => {
    const nextPatternNo = String(initialPatternNo || "");
    const currentPatternNo = String(selectedPattern || "");

    if (currentPatternNo === nextPatternNo) return;

    const syncPresetFromGlobal = async () => {
      setSelectedPattern(nextPatternNo);

      setOctaveOffset(0);
      setTransposeOffset(0);

      if (!nextPatternNo) {
        setLastSavedPatternNo("");

        const emptyGrid = Array(stepCount)
          .fill()
          .map(() => Array(notes.length).fill(false));

        setGrid(emptyGrid);
        setCurrentBar(0);
        currentBarRef.current = 0;
        return;
      }

      const notesArray = await fetchNotesPianoRoll(nextPatternNo);

      const newGrid = buildRepeatingPresetGrid(notesArray);

      InstrumentAudioContext.stopAllNotes();

      setGrid(newGrid);
      setCurrentBar(0);
      currentBarRef.current = 0;

      if (isPlaying && !isGlobalPlaying) {
        startPlayback(newGrid, 0);
      }
    };

    syncPresetFromGlobal();
  }, [initialPatternNo]);
  const debouncedSetVolume = useCallback(
    debounce((newVolume) => {
      InstrumentAudioContext.setInstVolume(newVolume, currentInstrument);
      setIsVolumeChanging(false);
    }, 100),
    [],
  );
  const getTouchVelocity = () => {
    if (!touchMode) return 1;

    const maximumVolume = Math.max(0, Math.min(tempVolume, 1));

    if (maximumVolume === 0) return 0;

    // Minimum is half of the current volume.
    const minimumVolume = maximumVolume * 0.5;

    const randomizedVolume =
      minimumVolume + Math.random() * (maximumVolume - minimumVolume);

    // Convert the absolute level into a multiplier because
    // InstrumentAudioContext already applies the slider volume.
    return randomizedVolume / maximumVolume;
  };
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);

    setVolume(newVolume);
    setTempVolume(newVolume);
    setIsVolumeChanging(true);

    InstrumentAudioContext.setInstVolume(newVolume, currentInstrument);
  };

  useEffect(() => {
    InstrumentAudioContext.loadAudio(currentInstrument);
    return () => {
      InstrumentAudioContext.stopAllNotes();
    };
  }, [currentInstrument]);

  useEffect(() => {
    if (isPlaying) {
      startPlayback(grid, currentBarRef.current);
    } else {
      pausePlayback();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying) {
      startPlayback();
    }
  }, [bpm]);
  useEffect(() => {
    if (!isPlaying) return;

    InstrumentAudioContext.stopAllNotes();

    const resumeBar = currentBarRef.current;

    startPlayback(grid, resumeBar);
  }, [currentInstrument]);
  useEffect(() => {
    if (keyboardRef.current) {
      keyboardRef.current.scrollLeft =
        (keyboardRef.current.scrollWidth - keyboardRef.current.clientWidth) / 2;
    }
  }, []);
  useEffect(() => {
    setGrid((prev) => {
      if (stepCount === prev.length) return prev;

      const newGrid = Array(stepCount)
        .fill()
        .map((_, i) => prev[i] || Array(notes.length).fill(false));

      return newGrid;
    });
  }, [stepCount]);
  useEffect(() => {
    if (!isPlaying) return;

    InstrumentAudioContext.stopAllNotes();

    const resumeBar = currentBarRef.current;
    startPlayback(grid, resumeBar);
  }, [isSwingOn, swingAmount]);
  const makeScaleNotes = (scale) => {
    if (!scale) return [];

    const ascending = scale.arohanam.map((note, index) => {
      const octave = index === scale.arohanam.length - 1 ? 4 : 3;
      return applyCurrentOffsetsToNote(`${normalizeNote(note)}${octave}`);
    });

    const descending = scale.avarohanam.map((note, index) => {
      const octave = index === 0 ? 4 : 3;
      return applyCurrentOffsetsToNote(`${normalizeNote(note)}${octave}`);
    });

    const base = [...ascending, ...descending];

    return Array(stepCount)
      .fill(null)
      .map((_, index) => base[index % base.length]);
  };
  const sendLivePreviewToSequencer = (
    newGrid,
    name = "Live Melody Preview",
  ) => {
    onLoadRagaToPianoRoll?.({
      type: "preview",
      name,
      ragaName: name,
      patternNo: `melody-preview-${currentInstrument}`,
      grid: newGrid,
    });
  };
  const loadScaleIntoGrid = (scaleId) => {
    setSelectedRagaScale(scaleId);

    const scale = findScaleById(scaleId);
    if (!scale) return;
    setLastSavedPatternName(scale.name);
    const scaleNotes = makeScaleNotes(scale);

    const newGrid = Array(stepCount)
      .fill()
      .map(() => Array(notes.length).fill(false));

    scaleNotes.forEach((noteName, barIndex) => {
      const noteIndex = notes.findIndex((n) => n === noteName);
      if (noteIndex !== -1 && barIndex < stepCount) {
        newGrid[barIndex][noteIndex] = true;
      }
    });

    setGrid(newGrid);
    sendLivePreviewToSequencer(newGrid, scale.name);
    setCurrentBar(0);

    setCurrentBar(0);
    currentBarRef.current = 0;

    if (isPlaying) {
      InstrumentAudioContext.stopAllNotes();
      startPlayback(newGrid, 0);
    }
  };
  const handleRagaScaleChange = (e) => {
    if (trialDropdownLocked) {
      openPlansModal();
      return;
    }

    loadScaleIntoGrid(e.target.value);
  };
  const handlePointerDown = (note) => {
    setIsMouseDown(true);
    playNoteOnPointerDown(note);
    setActiveNotes([note]);
    if (isRecording && !recordingStarted) {
      setRecordingStarted(true);
      startRecording();
    }
    if (isRecording) {
      addNoteToGrid(note, currentBar);
    }
  };

  const handlePointerUp = () => {
    setIsMouseDown(false);
    InstrumentAudioContext.stopAllNotes();
    setActiveNotes([]);
  };

  const handlePointerEnter = (note) => {
    if (isMouseDown) {
      playNoteOnPointerDown(note);
      setActiveNotes((prevNotes) => [...prevNotes, note]);
      if (isRecording) {
        addNoteToGrid(note, currentBar);
      }
    }
  };

  const playNoteOnPointerDown = (note) => {
    const noteIndex = notes.findIndex((n) => n === note);
    InstrumentAudioContext.playNoteOnMouseDown(
      noteIndex,
      notes,
      currentInstrument,
      getTouchVelocity(),
    );
  };
  const makeChordNotesForMode = (progression, mode = chordMode) => {
    const chordNotes = [];

    progression.chords.forEach((chord) => {
      const shiftedNotes = chord.notes.map((note) =>
        applyCurrentOffsetsToNote(note),
      );

      if (mode === "chord") {
        shiftedNotes.forEach((note) => {
          chordNotes.push({
            note,
            step: chord.step,
          });
        });
      }

      if (mode === "arp") {
        const arpNotes =
          shiftedNotes.length === 3
            ? [
                shiftedNotes[0],
                shiftedNotes[1],
                shiftedNotes[2],
                shiftedNotes[1],
              ]
            : shiftedNotes.slice(0, 4);

        arpNotes.forEach((note, index) => {
          const step = chord.step + index;

          if (step < stepCount) {
            chordNotes.push({
              note,
              step,
            });
          }
        });
      }
      if (mode === "bass") {
        const rootNote = shiftedNotes[0];

        chordNotes.push({
          note: rootNote,
          step: chord.step,
        });
      }
      if (stepCount === 32) {
        const repeated = chordNotes.map((note) => ({
          ...note,
          step: note.step + 16,
        }));

        chordNotes.push(...repeated);
      }
    });

    return chordNotes;
  };

  const buildGridFromChordNotes = (chordNotes) => {
    const newGrid = Array(stepCount)
      .fill()
      .map(() => Array(notes.length).fill(false));

    chordNotes.forEach(({ note, step }) => {
      const noteIndex = notes.findIndex((n) => n === note);

      if (noteIndex !== -1 && step >= 0 && step < stepCount) {
        newGrid[step][noteIndex] = true;
      }
    });

    return newGrid;
  };
  const handlePatternChange = async (event) => {
    setOctaveOffset(0);
    setTransposeOffset(0);
    const patternId = event.target.value;

    setSelectedPattern(patternId);

    if (!patternId) {
      // Make the current melody a new unsaved pattern.
      // Do not clear the grid, stop playback, or reset the step.
      setSelectedPattern("");
      setLastSavedPatternNo("");

      await onPatternSelectChange({
        instrumentName: currentInstrument,
        patternNo: "",
        grid,
      });

      return;
    }

    const notesArray = await fetchNotesPianoRoll(patternId);

    const newGrid = buildRepeatingPresetGrid(notesArray);

    setGrid(newGrid);

    setCurrentBar(0);
    currentBarRef.current = 0;

    await onPatternSelectChange({
      instrumentName: currentInstrument,
      patternNo: patternId,
      grid: newGrid,
    });

    if (isPlaying) {
      InstrumentAudioContext.stopAllNotes();
      startPlayback(newGrid, 0);
    }
  };
  const expandToStepCount = (baseGrid, sourceLength = 16) => {
    if (!Array.isArray(baseGrid) || !baseGrid.length) {
      return Array(stepCount)
        .fill()
        .map(() => Array(notes.length).fill(false));
    }

    return Array(stepCount)
      .fill()
      .map((_, index) => {
        const sourceRow =
          baseGrid[index % sourceLength] || Array(notes.length).fill(false);

        // Copy the row so steps 17–32 do not share the same array reference.
        return [...sourceRow];
      });
  };
  const buildRepeatingPresetGrid = (notesArray) => {
    const parsedNotes = (notesArray || [])
      .map((savedNote) => {
        const [noteName, barValue] = savedNote.split(",");
        const sourceStep = parseInt(barValue, 10) - 1;
        const noteIndex = notes.findIndex((note) => note === noteName);

        return {
          noteIndex,
          sourceStep,
        };
      })
      .filter(
        ({ noteIndex, sourceStep }) => noteIndex !== -1 && sourceStep >= 0,
      );

    const highestSavedStep = parsedNotes.reduce(
      (highest, item) => Math.max(highest, item.sourceStep + 1),
      0,
    );

    const sourceLength = highestSavedStep > 16 ? 32 : 16;

    const sourceGrid = Array(sourceLength)
      .fill()
      .map(() => Array(notes.length).fill(false));

    parsedNotes.forEach(({ noteIndex, sourceStep }) => {
      if (sourceStep < sourceLength) {
        sourceGrid[sourceStep][noteIndex] = true;
      }
    });

    return expandToStepCount(sourceGrid, sourceLength);
  };
  const changePatternPreset = (direction) => {
    const sortedPatterns = [...patterns].sort(
      (a, b) => Number(a.patternNo) - Number(b.patternNo),
    );

    const patternNos = sortedPatterns.map((p) => String(p.patternNo));

    const currentIndex = patternNos.indexOf(String(selectedPattern));
    const safeIndex = currentIndex === -1 ? 0 : currentIndex;

    const nextIndex =
      direction === "next"
        ? (safeIndex + 1) % patternNos.length
        : (safeIndex - 1 + patternNos.length) % patternNos.length;

    const nextPatternNo = patternNos[nextIndex];

    handlePatternChange({
      target: {
        value: nextPatternNo,
      },
    });
  };
  const previewChordProgression = (progressionId, mode = chordMode) => {
    const progression = findChordProgressionById(progressionId);

    if (!progression) return;

    setLastSavedPatternName(progression.name);

    const chordNotes = makeChordNotesForMode(progression, mode);
    const newGrid = buildGridFromChordNotes(chordNotes);

    setGrid(newGrid);
    sendLivePreviewToSequencer(newGrid, progression.name);
    setCurrentBar(0);
    currentBarRef.current = 0;

    if (isPlaying) {
      InstrumentAudioContext.stopAllNotes();
      startPlayback(newGrid, 0);
    }
  };
  const handleChordProgressionChange = (e) => {
    if (trialDropdownLocked) {
      openPlansModal();
      return;
    }

    const progressionId = e.target.value;
    setSelectedChordProgression(progressionId);
    previewChordProgression(progressionId);
  };
  const getSwingDelay = (step) => {
    if (!isSwingOn) return 0;

    // Delay steps 2, 4, 6, 8... using zero-based indexes 1, 3, 5, 7...
    const isOffBeatStep = step % 2 === 1;

    if (!isOffBeatStep) return 0;

    const stepInterval = (60 / bpm) * 250;

    return stepInterval * swingAmount;
  };
  const startPlayback = (newGrid = grid, startBar = 0) => {
    const totalBars = stepCount;
    const interval = (60 / bpm) * 250;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    let bar = startBar;
    setCurrentBar(startBar);
    currentBarRef.current = bar;

    intervalRef.current = setInterval(() => {
      const notesForBar = newGrid[bar];
      const currentlyPlaying = [];

      const swingDelay = getSwingDelay(bar);

      notesForBar.forEach((active, noteIndex) => {
        if (!active) return;

        const note = notes[noteIndex];
        currentlyPlaying.push(note);

        setTimeout(() => {
          InstrumentAudioContext.playPianoRollNote(
            noteIndex,
            notes,
            currentInstrument,
            900,
            getTouchVelocity(),
          );
        }, swingDelay);
      });

      if (currentlyPlaying.length > 0) {
        setPlayingNotes(currentlyPlaying);
        setLastLedNotes(currentlyPlaying);
      }
      setCurrentBar(bar);
      currentBarRef.current = bar;

      const nextBar = (bar + 1) % totalBars;
      bar = nextBar;
    }, interval);
  };
  const pausePlayback = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // stop sound only, but keep LED + active notes visible
    InstrumentAudioContext.stopAllNotes();
  };
  const stopPlayback = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    currentBarRef.current = 0;
    setCurrentBar(0);
    InstrumentAudioContext.stopAllNotes();
    setPlayingNotes([]);
    setLastLedNotes([]);
  };

  const addNoteToGrid = (note, bar) => {
    const newGrid = [...grid];
    const noteIndex = notes.findIndex((n) => n === note);
    newGrid[bar][noteIndex] = true;
    setGrid(newGrid);
  };

  const startRecording = () => {
    setCurrentBar(0);
    const totalBars = stepCount;
    const interval = (60 / bpm) * 250;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setCurrentBar((prevBar) => {
        if (prevBar === totalBars - 1) {
          stopRecording();
          return 0;
        }
        return prevBar + 1;
      });
    }, interval);
  };

  const stopRecording = () => {
    setIsRecording(false);
    setRecordingStarted(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    InstrumentAudioContext.stopAllNotes();
  };
  const transposeNote = (note, semitones) => {
    const noteNames = [
      "C",
      "C#",
      "D",
      "D#",
      "E",
      "F",
      "F#",
      "G",
      "G#",
      "A",
      "A#",
      "B",
    ];
    const match = note.match(/^([A-G]#?)(\d)$/);
    if (!match) return note;

    const [, noteName, octaveStr] = match;
    const octave = parseInt(octaveStr, 10);
    const currentIndex = noteNames.indexOf(noteName);
    const absolute = octave * 12 + currentIndex + semitones;

    if (absolute < 0) return note;

    const newOctave = Math.floor(absolute / 12);
    const newNoteName = noteNames[absolute % 12];

    return `${newNoteName}${newOctave}`;
  };
  const applyCurrentOffsetsToNote = (note) => {
    const octaveShifted = transposeNote(note, octaveOffset * 12);
    return transposeNote(octaveShifted, transposeOffset);
  };
  const transposeGrid = (semitones) => {
    const newGrid = Array(stepCount)
      .fill()
      .map(() => Array(notes.length).fill(false));

    grid.forEach((bar, barIndex) => {
      bar.forEach((active, noteIndex) => {
        if (!active) return;

        const oldNote = notes[noteIndex];
        const newNote = transposeNote(oldNote, semitones);
        const newNoteIndex = notes.findIndex((n) => n === newNote);

        if (newNoteIndex !== -1) {
          newGrid[barIndex][newNoteIndex] = true;
        }
      });
    });

    setGrid(newGrid);
    sendLivePreviewToSequencer(
      newGrid,
      lastSavedPatternName || "Live Melody Preview",
    );
    if (isPlaying) {
      startPlayback(newGrid);
    }
  };
  const getNoteWithOctaveOffset = (note) => {
    const noteName = note.slice(0, -1);
    const octave = parseInt(note.slice(-1), 10) + octaveOffset;
    return `${noteName}${octave}`;
  };

  const handleKeyDown = (e) => {
    const note = keyToNoteMap[e.key];
    if (note) {
      const adjustedNote = getNoteWithOctaveOffset(note);
      if (!playingNotes.includes(adjustedNote)) {
        playNoteOnPointerDown(adjustedNote);
        setPlayingNotes((prevNotes) => [...prevNotes, adjustedNote]);
        if (isRecording) {
          if (!recordingStarted) {
            setRecordingStarted(true);
            startRecording();
          }
          addNoteToGrid(adjustedNote, currentBar);
        }
      }
    }
  };

  const handleKeyUp = (e) => {
    const note = keyToNoteMap[e.key];
    if (note) {
      const adjustedNote = getNoteWithOctaveOffset(note);
      InstrumentAudioContext.stopAllNotes();
      setPlayingNotes((prevNotes) =>
        prevNotes.filter((n) => n !== adjustedNote),
      );
    }
  };
  const DEFAULT_INSTRUMENT_PRESETS = [
    "Piano",
    "String",
    "Pads",
    "Pluck",
    "EPiano",
    "Guitar",
    "Voice",
    "Flute",
    "Saxophone",
    "Clarinet",
    "Trumpet",
    "Brass",
  ];

  const getAvailableInstruments = () => {
    const addedInstrumentNames = selectedInstruments.map(
      (inst) => inst.InsName,
    );
    return [
      ...new Set([...DEFAULT_INSTRUMENT_PRESETS, ...addedInstrumentNames]),
    ];
  };

  const changeInstrumentPreset = async (direction) => {
    const availableInstruments = getAvailableInstruments();

    const index = availableInstruments.indexOf(currentInstrument);
    const safeIndex = index === -1 ? 0 : index;

    const nextIndex =
      direction === "next"
        ? (safeIndex + 1) % availableInstruments.length
        : (safeIndex - 1 + availableInstruments.length) %
          availableInstruments.length;

    const previousInstrument = currentInstrument;
    const nextInstrument = availableInstruments[nextIndex];

    if (previousInstrument === nextInstrument) return;

    InstrumentAudioContext.stopAllNotes();
    await InstrumentAudioContext.loadAudio(nextInstrument);

    InstrumentAudioContext.setInstVolume(tempVolume, nextInstrument);

    // Update local modal immediately.
    setCurrentInstrument(nextInstrument);
    setVolume(tempVolume);
    setTempVolume(tempVolume);

    // Update the global instrument row.
    await onInstrumentChange(previousInstrument, nextInstrument);

    // Keep preview mapping synchronized too.
    onPreviewInstrumentChange?.(previousInstrument, nextInstrument);
  };
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [playingNotes, isRecording, currentBar]);

  useEffect(() => {
    InstrumentAudioContext.stopAllNotes();
    setPlayingNotes([]);
  }, [octaveOffset]);

  const shiftGridOctave = (direction) => {
    const newGrid = Array(stepCount)
      .fill()
      .map(() => Array(notes.length).fill(false));

    grid.forEach((bar, barIndex) => {
      bar.forEach((active, noteIndex) => {
        if (!active) return;

        const note = notes[noteIndex];
        const noteName = note.slice(0, -1);
        const octave = parseInt(note.slice(-1), 10);
        const shiftedNote = `${noteName}${octave + direction}`;
        const shiftedIndex = notes.findIndex((n) => n === shiftedNote);

        if (shiftedIndex !== -1) {
          newGrid[barIndex][shiftedIndex] = true;
        }
      });
    });

    setGrid(newGrid);
    sendLivePreviewToSequencer(
      newGrid,
      lastSavedPatternName || "Live Melody Preview",
    );
    if (isPlaying) {
      startPlayback(newGrid);
    }
  };

  const decreaseOctave = () => {
    setOctaveOffset((prevOffset) => Math.max(prevOffset - 1, -2));
    shiftGridOctave(-1);
  };

  const increaseOctave = () => {
    setOctaveOffset((prevOffset) => Math.min(prevOffset + 1, 2));
    shiftGridOctave(1);
  };

  const renderLEDIndicators = () => {
    const leds = [];

    for (let i = 0; i < stepCount; i++) {
      leds.push(
        <div key={i} className="led-step-wrap">
          <div className={`led1-mel ${i === currentBar ? "active" : ""}`}></div>
        </div>,
      );
    }

    return leds;
  };

  const renderKey = (note) => {
    const isPlaying = playingNotes.includes(note);
    return (
      <div key={note} className="key-wrapper">
        <div
          className={`key ${isNaturalNote(note) ? "white-key" : "black-key"} ${
            isPlaying ? "highlighted" : ""
          }`}
          data-note={note}
          onPointerDown={() => handlePointerDown(note)}
          onPointerEnter={() => handlePointerEnter(note)}
          onPointerUp={handlePointerUp}
        >
          <div className="note-label">{note}</div>
        </div>
      </div>
    );
  };

  const handleStartRecording = () => {
    setIsRecording(!isRecording);
    if (!isRecording) {
      setRecordingStarted(false);
      setGrid(
        Array(stepCount)
          .fill()
          .map(() => Array(generateNotes().length).fill(false)),
      );
    }
  };
  const handleSavePattern = async () => {
    setIsLoadingSave(true);

    try {
      const isTempPattern =
        String(selectedPattern).startsWith("melody-") ||
        String(selectedPattern).startsWith("loaded-");

      let savedPatternNo;

      if (selectedPattern && !isTempPattern) {
        const parsedPatternNo = parseInt(selectedPattern, 10);

        await savePianoRollPattern(
          parsedPatternNo,
          grid,
          notes,
          setSelectedPattern,
        );

        savedPatternNo = parsedPatternNo;
      } else {
        const latestPatterns = await fetchPianoRollPatterns();

        const maxPatternNo = latestPatterns.reduce(
          (max, pattern) => Math.max(max, Number(pattern.patternNo) || 0),
          0,
        );

        const newPatternNo = maxPatternNo + 1;

        await savePianoRollPattern(
          newPatternNo,
          grid,
          notes,
          setSelectedPattern,
        );

        savedPatternNo = newPatternNo;

        const refreshedPatterns = await fetchPianoRollPatterns();
        setPatterns(refreshedPatterns);
      }

      setSelectedPattern(savedPatternNo.toString());
      setLastSavedPatternNo(savedPatternNo.toString());

      onLoadRagaToPianoRoll?.({
        type: "saved",
        name: lastSavedPatternName || `Preset ${savedPatternNo}`,
        ragaName: lastSavedPatternName || `Preset ${savedPatternNo}`,
        patternNo: savedPatternNo.toString(),
        instrumentName: currentInstrument,
        grid,
      });

      toast.success(`Saved as Piano Roll Pattern ${savedPatternNo}`);
    } catch (error) {
      console.error("Failed to save melody pattern:", error);
      toast.error("Failed to save melody pattern.");
    } finally {
      setIsLoadingSave(false);
    }
  };

  const handleDeletePattern = async () => {
    const parsedPatternNo = parseInt(selectedPattern, 10);

    if (!parsedPatternNo) {
      alert("Invalid pattern number.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this pattern?")) {
      return;
    }

    setIsLoadingDelete(true);

    try {
      await deletePianoRollPattern(parsedPatternNo);

      // Remove it immediately from the MelodyModal dropdown.
      const refreshedPatterns = await fetchPianoRollPatterns();
      setPatterns(refreshedPatterns);

      setSelectedPattern("");
      setLastSavedPatternNo("");
      setGrid(
        Array(stepCount)
          .fill()
          .map(() => Array(notes.length).fill(false)),
      );

      setCurrentBar(0);
      currentBarRef.current = 0;

      InstrumentAudioContext.stopAllNotes();

      // Clear this deleted preset from the instrument in BeatSeq.
      await onPatternSelectChange({
        instrumentName: currentInstrument,
        patternNo: "",
        deletedPatternNo: parsedPatternNo,
        grid: null,
      });

      toast.success(`Preset ${parsedPatternNo} deleted`);
    } catch (error) {
      console.error("Failed to delete melody pattern:", error);
      toast.error("Failed to delete preset.");
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const debouncedSetBpm = useCallback(
    debounce((newBpm) => {
      setBpm(newBpm);
    }, 100),
    [],
  );

  const increaseBpm = () => {
    const newBpm = Math.min(bpm + 5, 300);
    debouncedSetBpm(newBpm);
  };

  const decreaseBpm = () => {
    const newBpm = Math.max(bpm - 5, 20);
    debouncedSetBpm(newBpm);
  };

  const isNaturalNote = (note) => {
    return !note.includes("#");
  };
  const handleClose = () => {
    setIsPlaying(false);
    stopPlayback();
    setCurrentBar(0);
    setPlayingNotes([]);
    InstrumentAudioContext.stopAllNotes();

    onPreviewInstrumentChange?.(instrument, instrument);

    onClose(lastSavedPatternNo || selectedPattern, null, lastSavedPatternName);
  };

  const handleStop = () => {
    setIsPlaying(false);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    currentBarRef.current = 0;
    setCurrentBar(0);
    setPlayingNotes([]);
    setLastLedNotes([]);
    InstrumentAudioContext.stopAllNotes();
  };
  const getAllRagaIds = () =>
    ragaScaleGroups.flatMap((group) => group.scales.map((scale) => scale.id));

  const getAllChordIds = () =>
    chordProgressionGroups.flatMap((group) =>
      group.progressions.map((progression) => progression.id),
    );
  const changeRagaPreset = (direction) => {
    if (trialDropdownLocked) {
      openPlansModal();
      return;
    }

    const ids = getAllRagaIds();
    if (!ids.length) return;

    const currentIndex = ids.indexOf(selectedRagaScale);
    const safeIndex = currentIndex === -1 ? 0 : currentIndex;

    const nextIndex =
      direction === "next"
        ? (safeIndex + 1) % ids.length
        : (safeIndex - 1 + ids.length) % ids.length;

    const nextId = ids[nextIndex];

    setSelectedRagaScale(nextId);
    loadScaleIntoGrid(nextId);
  };
  const changeChordPreset = (direction) => {
    if (trialDropdownLocked) {
      openPlansModal();
      return;
    }

    const ids = getAllChordIds();
    if (!ids.length) return;

    const currentIndex = ids.indexOf(selectedChordProgression);
    const safeIndex = currentIndex === -1 ? 0 : currentIndex;

    const nextIndex =
      direction === "next"
        ? (safeIndex + 1) % ids.length
        : (safeIndex - 1 + ids.length) % ids.length;

    const nextId = ids[nextIndex];

    setSelectedChordProgression(nextId);
    previewChordProgression(nextId, chordMode);
  };
  return (
    <div className="melody-modal">
      <div className="melody-modal-content">
        <div className="mel-mod-header">
          <h2>Hxabyte Keyboard</h2>

          <button className="close-button-melody" onClick={handleClose}>
            ×
          </button>
        </div>{" "}
        <div className="melody-modal-top-actions">
          {isTrial && (
            <div
              className="trial-timer-pill small clickable"
              onClick={() => navigate("/plans")}
            >
              Preset Trial: {Math.floor(trialSeconds / 60)}:
              {String(trialSeconds % 60).padStart(2, "0")} / 5:00
              <span>
                Raga & Chord presets lock after 5 mins every hour in Trial mode
              </span>
            </div>
          )}
        </div>{" "}
        <div className="melody-modal-header">
          <div className="melody-modal-title">
            {" "}
            <button
              className="preset-nav-btn"
              onClick={() => changeInstrumentPreset("prev")}
            >
              &lt;
            </button>
            <h3> {currentInstrument}</h3>
            <button
              className="preset-nav-btn"
              onClick={() => changeInstrumentPreset("next")}
            >
              &gt;
            </button>
            <span
              className={`title-note-led ${
                playingNotes.length || lastLedNotes.length ? "active" : ""
              }`}
            >
              {titleLedText}
            </span>{" "}
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={tempVolume}
              onChange={handleVolumeChange}
              className="volume-slider-mel"
            />
          </div>{" "}
          <div className="play-sec-mel">
            <button
              onClick={handleStartRecording}
              className={`record-toggle-button ${
                isRecording ? "stop-recording" : "start-recording"
              }`}
            >
              {isRecording ? (
                <FaStopCircle className="icon" />
              ) : (
                <FaMicrophone className="icon" />
              )}
            </button>
          </div>
          <div className="play-sec-mel">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="play-button-seq"
            >
              {isPlaying ? <FaPause /> : <FaPlay />}
            </button>
            <button onClick={handleStop} className="stop-button2">
              <FaStop />
            </button>{" "}
          </div>
          <div className="option-controls">
            <div className="bpm-controls">
              <button onClick={decreaseBpm} className="bpm-button">
                <FaMinus />
              </button>
              <span className="bpm-display">{bpm} BPM</span>
              <button onClick={increaseBpm} className="bpm-button">
                <FaPlus />
              </button>
            </div>
            <div className="octave-controls">
              {" "}
              OCT
              <button onClick={decreaseOctave} className="octave-button">
                <FaMinus />
              </button>
              <button onClick={increaseOctave} className="octave-button">
                <FaPlus />
              </button>
            </div>
            <div className="octave-controls">
              {" "}
              TRA
              <button
                onClick={() => {
                  setTransposeOffset((prev) => prev - 1);
                  transposeGrid(-1);
                }}
                className="octave-button"
              >
                <FaMinus />
              </button>
              <button
                onClick={() => {
                  setTransposeOffset((prev) => prev + 1);
                  transposeGrid(1);
                }}
                className="octave-button"
              >
                {" "}
                <FaPlus />
              </button>
            </div>{" "}
          </div>{" "}
        </div>
        <div className="melody-controls">
          <div className="octave-controls">
            <select
              className="roll-pattern-dropdown2"
              value={selectedRagaScale}
              onChange={handleRagaScaleChange}
              disabled={trialDropdownLocked}
            >
              <option value="">Select Raga / Scale</option>

              {ragaScaleGroups.map((group) => (
                <optgroup key={group.category} label={group.category}>
                  {group.scales.map((scale) => (
                    <option key={scale.id} value={scale.id}>
                      {scale.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <button
              className="preset-nav-btn"
              onClick={() => changeRagaPreset("prev")}
              disabled={trialDropdownLocked}
            >
              &lt;
            </button>

            <button
              className="preset-nav-btn"
              onClick={() => changeRagaPreset("next")}
              disabled={trialDropdownLocked}
            >
              &gt;
            </button>
          </div>
          <div className="octave-controls">
            <select
              className="roll-pattern-dropdown2"
              value={selectedChordProgression}
              onChange={handleChordProgressionChange}
              disabled={trialDropdownLocked}
            >
              <option value="">Select Chord Prgsn</option>

              {chordProgressionGroups.map((group) => (
                <optgroup key={group.category} label={group.category}>
                  {group.progressions.map((progression) => (
                    <option key={progression.id} value={progression.id}>
                      {progression.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <button
              className="preset-nav-btn"
              onClick={() => changeChordPreset("prev")}
              disabled={trialDropdownLocked}
            >
              &lt;
            </button>

            <button
              className="preset-nav-btn"
              onClick={() => changeChordPreset("next")}
              disabled={trialDropdownLocked}
            >
              &gt;
            </button>
          </div>
          <button
            onClick={() => {
              const nextMode =
                chordMode === "chord"
                  ? "arp"
                  : chordMode === "arp"
                    ? "bass"
                    : "chord";

              setChordMode(nextMode);

              if (selectedChordProgression) {
                previewChordProgression(selectedChordProgression, nextMode);
              }
            }}
            className="octave-button"
          >
            {chordMode === "chord"
              ? "CHD"
              : chordMode === "arp"
                ? "ARP"
                : "BAS"}
          </button>{" "}
          <button
            type="button"
            title="Touch-sensitive note volume"
            aria-label="Toggle touch-sensitive note volume"
            aria-pressed={touchMode}
            onClick={() => setTouchMode((previous) => !previous)}
            className={`touch-button ${touchMode ? "active" : ""}`}
          >
            T
          </button>
          <div className="octave-controls">
            <select
              className="instrument-dropdown"
              value={selectedPattern}
              onChange={handlePatternChange}
            >
              <option value="">Select Preset</option>
              {patterns.map((pattern) => (
                <option key={pattern.$id} value={pattern.patternNo}>
                  Preset: {pattern.patternNo}
                </option>
              ))}
            </select>{" "}
            <button
              className="preset-nav-btn"
              onClick={() => changePatternPreset("prev")}
            >
              &lt;
            </button>
            <button
              className="preset-nav-btn"
              onClick={() => changePatternPreset("next")}
            >
              &gt;
            </button>{" "}
            <button
              onClick={handleSavePattern}
              className="save-pattern-button"
              disabled={isLoadingSave}
            >
              {isLoadingSave ? (
                <ClipLoader size={20} color={"#fff"} />
              ) : (
                <FaSave />
              )}
            </button>
            <button
              onClick={handleDeletePattern}
              className="delete-pattern-button"
              disabled={isLoadingDelete}
            >
              {isLoadingDelete ? (
                <ClipLoader size={20} color={"#fff"} />
              ) : (
                <FaTrash />
              )}
            </button>{" "}
          </div>
        </div>
        <div className="led-indicator"> {renderLEDIndicators()}</div>
        <div className="keyboard-container" ref={keyboardRef}>
          <div className="keyboard">{notes.map((note) => renderKey(note))}</div>
        </div>
      </div>
    </div>
  );
};

export default MelodyModal;
